/**
 * Utilidades para el manejo de imágenes y compresión
 */

import imageCompression from "browser-image-compression";

/**
 * Valida un archivo de imagen antes del procesamiento
 * @param {File} file - El archivo a validar
 * @throws {Error} Si el archivo no es válido
 */
export const validateImageFile = (file) => {
  if (!file) {
    throw new Error("Archivo no válido: archivo nulo o indefinido");
  }
  
  if (!file.type || !file.type.startsWith('image/')) {
    throw new Error(`Archivo no válido: ${file.name} no es una imagen válida`);
  }
  
  // Verificar tipos de imagen soportados
  const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!supportedTypes.includes(file.type.toLowerCase())) {
    throw new Error(`Formato no soportado: ${file.type}. Formatos soportados: JPEG, PNG, WebP`);
  }
  
  // Verificar tamaño máximo (50MB como límite absoluto)
  const maxSizeBytes = 50 * 1024 * 1024; // 50MB
  if (file.size > maxSizeBytes) {
    throw new Error(`Archivo demasiado grande: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB). Máximo permitido: 50MB`);
  }
  
  return true;
};

/**
 * Comprime una imagen con reintentos y fallback
 * @param {File} file - El archivo a comprimir
 * @param {Object} options - Opciones de compresión
 * @param {number} retries - Número de reintentos
 * @returns {Promise<File>} El archivo comprimido
 */
export const compressImageWithRetry = async (file, options = {}, retries = 3) => {
  // Detectar si es Android Chrome para aplicar configuraciones específicas
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isChrome = /Chrome/i.test(navigator.userAgent);
  const isAndroidChrome = isAndroid && isChrome;
  
  // Configuración específica para Android Chrome (más agresiva)
  const androidOptions = {
    maxSizeMB: 1.2, // Reducir aún más el tamaño objetivo
    maxWidthOrHeight: 1024, // Reducir resolución
    initialQuality: 0.65, // Bajar calidad inicial
    useWebWorker: false, // Deshabilitar Web Workers en Android Chrome
    alwaysKeepResolution: false,
    preserveExif: false,
  };
  
  const defaultOptions = {
    maxSizeMB: 3,
    maxWidthOrHeight: 1920,
    initialQuality: 0.8,
    useWebWorker: false,
    alwaysKeepResolution: false,
    preserveExif: false,
  };

  // Usar configuración específica para Android Chrome
  const baseOptions = isAndroidChrome ? androidOptions : defaultOptions;
  const compressionOptions = { ...baseOptions, ...options };
  
  // Más reintentos para Android Chrome
  const maxRetries = isAndroidChrome ? 5 : retries;

  console.log(`Iniciando compresión para Android Chrome: ${isAndroidChrome}, reintentos: ${maxRetries}`);
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Intento ${attempt}/${maxRetries} de compresión para: ${file.name}`, compressionOptions);
      
      const compressedFile = await imageCompression(file, compressionOptions);
      
      if (!compressedFile) {
        throw new Error("La compresión devolvió un archivo nulo");
      }
      
      console.log(`Compresión exitosa: ${file.name} (${file.size} -> ${compressedFile.size} bytes)`);
      return compressedFile;
      
    } catch (error) {
      console.error(`Error en intento ${attempt} de compresión para ${file.name}:`, error);
      
      if (attempt === maxRetries) {
        // En el último intento, intentar con configuración más permisiva
        try {
          console.log(`Último intento con configuración permisiva para: ${file.name}`);
          const fallbackOptions = {
            maxSizeMB: 0.8, // Ultra agresivo en Android para evitar errores de red
            maxWidthOrHeight: 800,
            initialQuality: 0.55,
            useWebWorker: false,
          };
          
          const fallbackFile = await imageCompression(file, fallbackOptions);
          if (fallbackFile) {
            console.log(`Compresión de fallback exitosa para: ${file.name}`);
            return fallbackFile;
          }
        } catch (fallbackError) {
          console.error(`Error en compresión de fallback para ${file.name}:`, fallbackError);
        }
        
        // Si todo falla, usar el archivo original (si es pequeño)
        if (file.size < 3 * 1024 * 1024) { // Menos de 3MB
          console.log(`Usando archivo original sin comprimir: ${file.name}`);
          return file;
        }
        
        throw new Error(`Error al procesar imagen después de ${maxRetries} intentos: ${error.message || 'Error desconocido en compresión'}`);
      }
      
      // Esperar antes del siguiente intento (backoff exponencial)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
};

/**
 * Obtiene la configuración de compresión desde las variables de entorno
 * @returns {Object} Configuración de compresión
 */
export const getCompressionConfig = () => {
  return {
    maxWidth: parseInt(process.env.REACT_APP_IMAGE_MAX_WIDTH) || 1920,
    quality: parseFloat(process.env.REACT_APP_IMAGE_COMPRESSION_QUALITY) || 0.8,
  };
};

/**
 * Genera un nombre de archivo único
 * @param {File} file - El archivo original
 * @param {string} orderId - ID de la orden
 * @param {number} index - Índice del archivo
 * @returns {string} Nombre de archivo único
 */
export const generateUniqueFileName = (file, orderId, index = 0) => {
  const fileNamePart = file?.name
    ? file.name.toLowerCase().replace(/\s/g, "_")
    : `image_${Date.now()}_${index}.jpg`;
  
  return `${orderId}/${Date.now()}-${fileNamePart}`;
};

/**
 * Maneja errores específicos de procesamiento de imágenes
 * @param {Error} error - El error original
 * @returns {Object} Información del error procesada
 */
export const handleImageError = (error) => {
  const errorMessage = error.message || 'Error desconocido';
  
  if (errorMessage.includes("Archivo no válido") || errorMessage.includes("Formato no soportado")) {
    return {
      type: 'validation',
      message: 'Error con los archivos de imagen. Por favor, verifica que sean imágenes válidas en formato JPEG, PNG o WebP.',
      userMessage: 'Los archivos seleccionados no son imágenes válidas. Por favor, selecciona solo archivos de imagen en formato JPEG, PNG o WebP.'
    };
  }
  
  if (errorMessage.includes("demasiado grande")) {
    return {
      type: 'size',
      message: errorMessage,
      userMessage: 'El archivo es demasiado grande. Por favor, selecciona una imagen de menos de 50MB.'
    };
  }
  
  if (errorMessage.includes("compresión") || errorMessage.includes("procesar imagen")) {
    return {
      type: 'compression',
      message: errorMessage,
      userMessage: 'Error al procesar las imágenes. Por favor, verifica que los archivos no estén corruptos e intenta nuevamente.'
    };
  }
  
  return {
    type: 'unknown',
    message: errorMessage,
    userMessage: 'Error inesperado al procesar las imágenes. Por favor, intenta nuevamente.'
  };
};
